import { useNow } from '@/hooks/useNow'
import { formatBytes, formatEta, relativeTime } from '@/lib/format'
import type { MediaRequest, RequestStage } from '@/lib/types'
import { StageBadge } from './Badge'
import { PosterImage } from './PosterImage'
import { ProgressBar } from './ProgressBar'
import { StaleBadge } from './StaleNotice'

/*
 * The route every request travels. This is a real sequence — a request cannot
 * reach a later stop without passing the earlier ones — which is what earns it
 * a positional device rather than a plain label.
 */
const ROUTE: { stage: RequestStage; label: string }[] = [
  { stage: 'pending', label: 'Requested' },
  { stage: 'approved', label: 'Approved' },
  { stage: 'downloading', label: 'Downloading' },
  { stage: 'available', label: 'Available' },
]

/**
 * Compact route rail: which stop this request has reached.
 *
 * Deliberately shown only while something is still in flight. Most of a mature
 * library is `available`, and drawing a completed rail on every finished row
 * would be decoration — the flap already says "Ready". It appears exactly where
 * it answers a question the flap cannot: how much further is there to go.
 */
function RouteRail({ stage }: { stage: RequestStage }) {
  const currentIndex = ROUTE.findIndex((stop) => stop.stage === stage)

  return (
    <ol
      className="flex items-center gap-1"
      aria-label={`Stage ${currentIndex + 1} of ${ROUTE.length}: ${ROUTE[Math.max(currentIndex, 0)]?.label}`}
    >
      {ROUTE.map((stop, index) => {
        const reached = index <= currentIndex
        const isCurrent = index === currentIndex
        return (
          <li key={stop.stage} className="flex items-center gap-1">
            <span
              className={`size-1.5 rounded-full transition-colors ${
                isCurrent ? 'bg-accent' : reached ? 'bg-accent/45' : 'bg-line-strong'
              }`}
            />
            {index < ROUTE.length - 1 ? (
              <span aria-hidden className={`h-px w-4 sm:w-6 ${reached ? 'bg-accent/35' : 'bg-line'}`} />
            ) : null}
          </li>
        )
      })}
      <li aria-hidden className="signage ml-1.5 text-[9px] text-faint">
        {ROUTE[Math.max(currentIndex, 0)]?.label}
      </li>
    </ol>
  )
}

function downloadDetail(request: MediaRequest): string | null {
  const download = request.download
  if (!download) return null

  const eta = formatEta(download.eta)
  const remaining =
    download.sizeLeft > 0 && download.size > 0 ? `${formatBytes(download.sizeLeft)} left` : null
  // Speed is the most obviously *live* number available, so it leads: a moving
  // rate reads as "this is happening now" in a way a percentage does not.
  const speed = download.speed ? `${formatBytes(download.speed)}/s` : null

  return [speed, eta ? `${eta} left` : null, remaining].filter(Boolean).join(' · ') || null
}

/** True while the request is still moving and the rail carries information. */
function inFlight(stage: RequestStage): boolean {
  return stage === 'pending' || stage === 'approved' || stage === 'downloading'
}

/** "12 Sep" for a date this year, "Sep 2027" when it is further out. */
function formatRelease(value: string): string | null {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const sameYear = date.getUTCFullYear() === new Date().getUTCFullYear()
  return date.toLocaleDateString(undefined, {
    ...(sameYear ? { day: 'numeric', month: 'short' } : { month: 'short', year: 'numeric' }),
    timeZone: 'UTC',
  })
}

export function RequestCard({ request, index = 0 }: { request: MediaRequest; index?: number }) {
  const now = useNow(60_000)
  const download = request.stage === 'available' ? null : request.download

  /*
   * Unreleased titles are quieted rather than hidden.
   *
   * They are not stuck and nobody needs to act on them — they are waiting on
   * the world — so they should not compete with a live download for attention.
   * But they are still the user's request, and removing them entirely reads as
   * "you lost it", so the row shrinks and dims instead of disappearing.
   */
  const awaiting = request.awaitingRelease === true
  const releaseLabel = awaiting && request.releaseDate ? formatRelease(request.releaseDate) : null

  return (
    <article
      className={`board-row flex bg-surface transition-colors hover:bg-elevated ${
        awaiting ? 'gap-2.5 px-3 py-2 opacity-60 sm:px-4' : 'gap-3 px-3 py-3 sm:gap-4 sm:px-4'
      }`}
    >
      <PosterImage
        src={request.posterUrl}
        title={request.title}
        mediaType={request.mediaType}
        className={
          awaiting
            ? 'h-12 w-8 shrink-0 rounded-[3px]'
            : 'h-21 w-14 shrink-0 rounded-[3px] sm:h-27 sm:w-18'
        }
      />

      <div className={`flex min-w-0 flex-1 flex-col ${awaiting ? 'justify-center gap-0.5' : 'gap-1.5'}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className={`signage-tight min-w-0 text-ink ${awaiting ? 'text-xs' : 'text-sm'}`}>
            {request.title}
            {/* Several seasons of one show can be requested separately; without
                this they are indistinguishable rows with the same name. */}
            {request.seasonLabel ? (
              <span className="ml-1.5 font-mono text-[11px] font-normal text-accent">
                {request.seasonLabel}
              </span>
            ) : null}
          </h3>
          <div className="flex shrink-0 items-center gap-1.5">
            {request.stale ? <StaleBadge /> : null}
            {awaiting ? (
              <span className="signage rounded-[3px] border border-line-strong px-1.5 py-0.5 text-[9px] text-faint">
                Not out yet
              </span>
            ) : (
              /* Staggered by row so a list sets itself the way a board does. */
              <StageBadge stage={request.stage} delayMs={Math.min(index, 12) * 45} />
            )}
          </div>
        </div>

        <p className="font-mono text-[11px] text-faint">
          {request.mediaType === 'tv' ? 'Series' : 'Film'}
          {releaseLabel ? ` · out ${releaseLabel}` : ` · ${relativeTime(request.requestedAt, now)}`}
          {!awaiting && request.availableAt
            ? ` · ready ${relativeTime(request.availableAt, now)}`
            : ''}
        </p>

        {/* The route rail would imply a journey that has not started. */}
        {inFlight(request.stage) && !awaiting ? (
          <div className="mt-0.5">
            <RouteRail stage={request.stage} />
          </div>
        ) : null}

        {download && request.stage === 'downloading' ? (
          <div className="mt-1">
            <ProgressBar
              value={download.progress}
              label={download.status || 'Downloading'}
              detail={downloadDetail(request)}
              indeterminate={download.progress <= 0}
              tone="accent"
            />
          </div>
        ) : request.stage === 'failed' ? (
          <p className="mt-0.5 text-xs text-danger">
            This one failed to download. An admin has been notified.
          </p>
        ) : request.stage === 'declined' ? (
          <p className="mt-0.5 text-xs text-muted">An admin declined this request.</p>
        ) : null}
      </div>
    </article>
  )
}
